import { useEffect, DependencyList } from 'react'
import { observe } from 'mobx'
import { useAppState } from './use-mobile-service'

/**
 * Runs a callback on mount and whenever the active domain changes.
 * Combines React's useEffect with MobX's observe() for domain reactivity.
 *
 * Replaces the repeated pattern:
 * ```
 * useEffect(() => {
 *     fetchData()
 *     const dispose = observe(appState, 'currentDomainJsonData', () => fetchData())
 *     return () => dispose()
 * }, [fetchData, appState])
 * ```
 *
 * @param callback - Function to run on mount and domain change. May return a cleanup function.
 * @param deps - Additional dependencies (beyond domain change) that should re-trigger the effect.
 */
export function useDomainEffect(callback: () => void | (() => void), deps: DependencyList = []): void {
    const appState = useAppState()

    useEffect(() => {
        const cleanup = callback()

        const dispose = observe(appState, 'currentDomainJsonData', () => {
            callback()
        })

        return () => {
            dispose()
            if (typeof cleanup === 'function') cleanup()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appState, ...deps])
}
